import React from 'react';
import clsx from 'clsx';

import Typography from 'components/ui/Typography';
import { TextStyle } from 'components/ui/Typography/Typography';
import { Color } from 'types/colors';

import css from './Button.module.scss';

interface Props extends React.PropsWithChildren {
    color?: 'blue' | 'pale' | 'transparent' | 'red' | 'green' | 'white' | 'dark-blue' | 'purple';
    beforeContent?: React.ReactNode;
    variant?: 'approve' | 'warn' | 'delete';
    afterContent?: React.ReactNode;
    blocked?: boolean;
    className?: string;
    withPadding?: boolean;
    textStyle?: TextStyle;
    textColor?: Color | 'inherit';
    iconButton?: boolean;
    // Click area extends beyond button's margin instead in order to simplify layout (no padding)
    // Caution: Use wisely, since this can cause overlap with other elements
    outerClickArea?: boolean;
}

const Button: React.FC<Props & JSX.IntrinsicElements['button']> = ({
    children,
    color = 'blue',
    beforeContent,
    variant,
    afterContent,
    blocked,
    className,
    withPadding,
    textStyle = 'ButtonMedSemibold',
    textColor = 'inherit',
    iconButton,
    outerClickArea = false,
    ...rest
}) => {
    const classNames = clsx(
        css.button,
        color && !variant && css[color],
        blocked && css.blocked,
        variant && css[`variant-${variant}`],
        className && className,
        withPadding && css.withPadding,
        iconButton && css.iconButton,
        iconButton && outerClickArea && css.iconButtonOuterClickArea,
    );

    const content = iconButton ? (
        children
    ) : (
        <Typography tag="span" color={textColor} ellipsis style={textStyle}>
            {children}
        </Typography>
    );

    return (
        <button className={classNames} {...rest}>
            <>
                {beforeContent}
                {content}
                {afterContent}
            </>
        </button>
    );
};

export default React.memo(Button);
